import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata
} from '../../client-server-common';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {doppeHideableValueCreateHidden} from '../../client-server-common/types/doppe-hideable-value';
import type {WixImageResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {SerializedDateTimeValueAsString, RichTextContent } from '@wix/devzai-utils-common';
import {asArray, dateGetCurrentDateTime, dateSerializedToString, immerProduce} from '@wix/devzai-utils-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export namespace DoppeActionTypeTestimonial {
    export interface ActionSettings extends
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        name: string;
        tagLine: DoppeHideableValue<string>;
        date: DoppeHideableValue<SerializedDateTimeValueAsString>;
        mainQuote: DoppeHideableValue<string>;
        description: RichTextContent;
        profileMedia: WixImageResource | null;
        rating:  DoppeHideableValue<1 | 2 | 3 | 4 | 5>;
        ribbon: DoppeHideableValue<string>;
    }
}

export const doppeActionTypeTestimonial = doppeActionTypeDefineMetadata<DoppeActionTypeTestimonial.ActionSettings>({
    id: 'io.bymo.action/testimonial',
    resolveActionSettings: action => {

        const {
            sections,
            ctaButtons,
            ...restSettings
        } = action.settings;

        return {
            name: '',
            description:  '',
            tagLine: '',
            date: doppeHideableValueCreateHidden(dateSerializedToString(dateGetCurrentDateTime())),
            ribbon: doppeHideableValueCreateHidden(''),
            mainQuote: doppeHideableValueCreateHidden(''),
            profileMedia: null,
            rating: 5,
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
                ctaButtons: ctaButtons,
            }, false),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            tagLine: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            date: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityPublishDate),
            mainQuote: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            profileMedia: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            rating: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Rating, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                date: true,
                description: true,
                mainQuote: true,
                rating: true,
                ribbon: true,
                tagLine: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: false,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedMedia: actionSettings => {
        return actionSettings.profileMedia ? asArray(actionSettings.profileMedia) :  [];
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.profileMedia ?? null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.profileMedia) {
                actionSettings.profileMedia = wixMediaResourceReplaceExternalImage(actionSettings.profileMedia, externalImagesUrlToImageResourceMap)
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
});


